import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { FocusEvent, ReactNode } from 'react';
import { mountedRows, useScrollWindow } from '../sessions/virtual.ts';
import { ESTIMATED_CARD_PX, GRID_GAP_PX, nextSlot, rowWindow, useGridColumns } from './grid.ts';

/** Rows mounted beyond each edge of the viewport, in px: enough for a fast flick between frames. */
const OVERSCAN_PX = 800;

export interface VirtualMachineGridProps<T extends { id: string }> {
  items: T[];
  renderItem: (item: T) => ReactNode;
}

/**
 * The Machines grid, windowed: the same columns as `grid-cols-1 md:grid-cols-2 xl:grid-cols-3`,
 * one positioned row of cards per grid row, of which only those near the viewport are mounted.
 * The card holding keyboard focus stays mounted when it scrolls away.
 */
export function VirtualMachineGrid<T extends { id: string }>({ items, renderItem }: VirtualMachineGridProps<T>) {
  const ref = useRef<HTMLDivElement>(null);
  const cols = useGridColumns();
  const [slot, setSlot] = useState(ESTIMATED_CARD_PX);
  const measured = useRef(false);
  const win = useScrollWindow(ref);
  const rows = Math.ceil(items.length / cols);
  const pitch = slot + GRID_GAP_PX;
  const total = rows ? rows * pitch - GRID_GAP_PX : 0;
  const [start, end] = rowWindow(rows, slot, GRID_GAP_PX, win.top - OVERSCAN_PX, win.top + win.height + OVERSCAN_PX);

  const [focusedId, setFocusedId] = useState<string | null>(null);
  const focusedRow = useMemo(() => {
    if (!focusedId) return -1;
    const i = items.findIndex((x) => x.id === focusedId);
    return i < 0 ? -1 : Math.floor(i / cols);
  }, [items, focusedId, cols]);
  const onFocus = (e: FocusEvent<HTMLDivElement>) => setFocusedId((e.target as HTMLElement).closest('[data-grid-item]')?.getAttribute('data-grid-item') ?? null);
  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocusedId(null);
  };

  // Every row gets the height of the tallest mounted row: exact on the first measure, then only growing.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let tallest = 0;
    for (const r of el.querySelectorAll<HTMLElement>(':scope > [data-grid-row]')) tallest = Math.max(tallest, r.getBoundingClientRect().height);
    const next = nextSlot(slot, tallest, measured.current);
    if (tallest > 0) measured.current = true;
    if (next !== null) setSlot(next);
  });

  return (
    <div ref={ref} className="relative" style={{ height: total }} onFocus={onFocus} onBlur={onBlur}>
      {mountedRows(start, end, focusedRow).map((r) => (
        <div
          key={r}
          data-grid-row
          className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
          style={{ position: 'absolute', top: r * pitch, left: 0, right: 0 }}
        >
          {items.slice(r * cols, r * cols + cols).map((it) => (
            <div key={it.id} data-grid-item={it.id} className="min-w-0">
              {renderItem(it)}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
